import CurrencyFormatter from '@/commons/modules/currency/CurrencyFormatter';
import currencyConfig from './currencyConfig.json';

export type CurrencyFormatterConfig = [
  number, // Decimal count
  string, // Decimal separator
  string, // Thousand separator
  string, // Currency symbol
  boolean, // Currency before value
  boolean, // Should remove trailing zeros
];

export type CurrencyLanguageConfig = {
  [key: string]: CurrencyFormatterConfig,
  default: CurrencyFormatterConfig,
}

export type TenantCurrencyConfig = {
  [key: string]: Partial<CurrencyLanguageConfig>,
  default: CurrencyLanguageConfig,
}

export interface CurrencyConfig {
  [key: string]: Partial<TenantCurrencyConfig>,
  default: TenantCurrencyConfig,
}

export interface FormatterOptions {
  omitDecimals?: boolean,
  omitDecimalSeparator?: boolean,
  omitThousandsSeparator?: boolean,
  omitCurrencySymbol?: boolean,
  useCurrencyCode?: boolean,
}

// Temp config lookup until a better solution is implemented via API or dynamic import. TBD.
function getFormatterConfig(tenant: string, currency: string, language: string) {
  const config = currencyConfig as unknown as CurrencyConfig;

  const configByTenant = config[tenant] || config.default;

  const configByCurrency = configByTenant[currency] // Tenant > currency
    || configByTenant.default // Tenant > default currency
    || config.default[currency] // Default tenant > currency
    || config.default.default; // Default tenant > default currency

  return configByCurrency[language] // Currency > lang
    || configByCurrency.default // Currency > default lang
    || config.default[currency]?.default // Default tenant > currency > default lang
    || config.default.default.default; // Default tenant > default currency > default lang
}

class Config {
  tenant!: string;
  language!: string;

  constructor(tenant: string, language: string) {
    this.tenant = tenant
    this.language = language
  }
}

interface CurrencyConfigOptions {
  tenant?: string;
  currency?: string;
  language?: string;
}

export default class Currency {
  config!: Config;
  decimals!: number;
  decimalsSeparator!: string;
  thousandsSeparator!: string;
  currencySymbol!: string;
  currencyBeforeValue!: boolean;
  shouldRemoveTrailingZeros!: boolean;
  tenant!: string;
  currency!: string;
  language!: string;

  constructor() {
    this.setConfig({ tenant: '', currency: '', language: '' });
  }

  public resolveCurrency(currencyCode: string) {
    const currency = new Currency();
    const options = {
      tenant: this.tenant,
      currency: currencyCode,
      language: this.config.language,
    };
    currency.setConfig(options);
    return currency;
  }

  public setConfig({
    tenant = '',
    currency = '',
    language = '',
  }: CurrencyConfigOptions) {
    const formatterConfig = getFormatterConfig(tenant, currency, language);

    const [
      decimals,
      decimalsSeparator,
      thousandsSeparator,
      currencySymbol,
      currencyBeforeValue,
      shouldRemoveTrailingZeros,
    ] = formatterConfig;

    this.config = new Config(tenant, language);
    this.decimals = decimals;
    this.decimalsSeparator = decimalsSeparator;
    this.thousandsSeparator = thousandsSeparator;
    this.currencySymbol = currencySymbol;
    this.currencyBeforeValue = currencyBeforeValue;
    this.shouldRemoveTrailingZeros = shouldRemoveTrailingZeros;
    this.tenant = tenant;
    this.currency = currency;
    this.language = language;
  }

  public getDecimals() {
    return this.decimals;
  }

  public format(amount: number, options: FormatterOptions = {}) {
    const currencySymbol = options.useCurrencyCode
      ? this.currency
      : this.currencySymbol;

    const currencyFormatter = new CurrencyFormatter(amount)
      .withDecimals(options.omitDecimals ? 0 : this.decimals)
      .withDecimalSeparator(
        options.omitDecimalSeparator ? '' : this.decimalsSeparator,
      )
      .withThousandsSeparator(
        options.omitThousandsSeparator ? '' : this.thousandsSeparator,
      )
      .withCurrencySymbol(options.omitCurrencySymbol ? '' : currencySymbol)
      .withCurrencyBeforeValue(this.currencyBeforeValue)
      .withDecimalTrimming(this.shouldRemoveTrailingZeros);

    return currencyFormatter.format();
  }

  public breakdown(
    total: number,
    taxPercentage: number = 21,
    formatterOptions?: FormatterOptions,
  ): { basePrice: string; taxes: string } {
    const basePrice = (total * (100 - taxPercentage)) / 100;
    const basePriceRounded = Math.round(basePrice * 100) / 100;
    const taxes = total - basePriceRounded;

    return {
      basePrice: this.format(basePriceRounded, formatterOptions),
      taxes: this.format(taxes, formatterOptions),
    };
  }

  public getCurrencySymbol(currencyName: string) {
    const symbol = (<Record<string, any>>currencyConfig.default)?.[currencyName]
      .default[3];

    return symbol;
  }

  public removeThousandSeparators(price: string) {
    const hasComma = price.includes(',');
    const hasDot = price.includes('.');
    if (hasComma && hasDot) {
      if (price.lastIndexOf(',') > price.lastIndexOf('.')) {
        return price.replaceAll('.', '').replace(',', '.');
      }
      return price.replaceAll(',', '');
    }
    if (hasComma) {
      const parts = price.split(',');
      const allThousands = parts.slice(1).every((p) => p.length === 3);
      return allThousands
        ? price.replaceAll(',', '')
        : price.replace(',', '.');
    }
    return price;
  }
}
